import { t } from "i18next"
import { capitalizeFirstLetter, convertToReadableString, joinBytItems, localisedValue } from "utils"
import dayjs from "dayjs"
import { classifications } from "features/shared/data"
import { DateFormat, Flex, Tag, Text } from "components/common"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import theme from "utils/themeVars"
import { Separator } from "components/custom"
import MeetingTaskStatus from "features/tasks/components/taskStatus/meetingTaskStatus"

export const getEntityCardSectionsConfig = () => ({
  client: {
    sectionTitle: t("Client Details"),
    fields: [
      { key: "clientAddedDate", label: t("Created At"), getValue: d => d?.createdAt, type: "date" },
      {
        key: "Source",
        label: t("Source"),
        getValue: d => convertToReadableString(d?.referralType),
      },
      {
        key: "platform",
        label: t("Platform"),
        getValue: d => convertToReadableString(d?.source),
      },
    ],
  },
  lead: {
    sectionTitle: t("Lead Details"),
    fields: [
      {
        key: "leadId",
        label: t("Lead ID"),
        getValue: d => [d?.id, <DateFormat date={d?.createdAt} color={theme["gray900"]} />],
        type: "list",
      },
      {
        key: "interestedProject",
        label: t("Interest"),
        getValue: d => localisedValue({ object: d?.Project, key: "name" }) || "—",
      },
      {
        key: "classification",
        label: t("Classification"),
        getValue: d => ({
          icon: "MdLocalFireDepartment",
          color: classifications()?.[d?.InternalInquiryClassification?.id]?.style?.color,
          hasBackground: true,
          tooltipText: classifications()?.[d?.InternalInquiryClassification?.id]?.name,
          title: classifications()?.[d?.InternalInquiryClassification?.id]?.name,
        }),
        type: "iconText",
      },
      {
        key: "lastTaskNameDate",
        label: t("Last Task"),
        getValue: d => {
          const name =
            d?.InternalInquiryStatus?.key === "new"
              ? t("No Task Added")
              : localisedValue({ object: d?.InternalInquiryStatus, key: "name" })
          return [
            name,
            d?.LatestTask?.createdAt && <DateFormat date={d?.LatestTask?.createdAt} color={theme["gray900"]} />,
          ]
        },
        type: "list",
      },
    ],
  },
  meeting: {
    sectionTitle: t("Meeting Details"),
    fields: [
      {
        key: "taskName",
        label: t("Meeting Task Name"),
        getValue: d => {
          const parentName = localisedValue({ object: d?.TaskType?.Parent, key: "name" })
          const taskName = localisedValue({ object: d?.TaskType, key: "name" })
          return (
            <>
              {!!parentName && <>{parentName}</>}
              {!!parentName && !!taskName && " - "}
              {!!taskName && <>{taskName}</>}
              {d && <MeetingTaskStatus data={d} />}
            </>
          )
        },
      },
      {
        key: "meetingLocation",
        label: t("Meeting Location"),
        getValue: d =>
          localisedValue({ object: d?.AgencyOffice, key: "title" }) ||
          localisedValue({ object: d?.TaskProject, key: "name" }) ||
          (d?.latitude && d?.longitude && `${d?.latitude}, ${d?.longitude}`) ||
          "—",
      },
      {
        key: "meetingDateTime",
        label: t("Meeting Date/Time"),
        getValue: d => d?.startTime,
        type: "date",
      },
      {
        key: "visitDetails",
        label: t("Visit Details"),
        getValue: d => {
          const start = d?.startTime
          const end = d?.endTime
          if (start && end && dayjs(start).isValid() && dayjs(end).isValid()) {
            const duration = d?.duration ? ` · ${d.duration}` : ""
            return `${dayjs(start).format("hh:mm A")} – ${dayjs(end).format("hh:mm A")}${duration}`
          }
          return d?.duration || "—"
        },
      },
      {
        key: "taskAddedBy",
        label: t("Meeting task added by"),
        getValue: d => localisedValue({ object: d?.CreatedBy, key: "name" }) || "—",
      },
      {
        key: "userRemarks",
        label: t("User Remarks"),
        getValue: d => localisedValue({ object: d, key: "description" }) ?? "—",
        type: "textLong",
      },
      {
        key: "aiRemarks",
        label: t("AI Remarks"),
        getValue: d => localisedValue({ object: d, key: "aiDescription" }) ?? "—",
        type: "textLong",
      },
      {
        key: "meetingAttachment",
        label: t("Meeting attachment"),
        getValue: d => (d?.Attachments?.length ? d?.Attachments : null),
        type: "attachment",
      },
    ],
  },

  call: {
    sectionTitle: t("Call Details"),
    fields: [
      {
        key: "callDateTime",
        getValue: d => ({
          title: joinBytItems(
            [
              <Tag className="fs12" color={d?.tag?.color}>
                {d?.tag?.title}
              </Tag>,
              `Via ${d?.callBy}`,
            ],
            <Separator />
          ),
          icon: d?.callType === "in" ? "IconCallIncoming" : "IconCallOutgoing",
          date: d?.callTime,
          message: convertToReadableString(d?.callType),
          color: d?.callType === "in" ? "#0186fe" : theme["primary-color"],
        }),
        type: "iconTextDate",
      },
      {
        key: "callRecording",
        label: t("Call recording"),
        getValue: d => d?.recording ?? null,
        type: "recording",
      },
      {
        key: "caller",
        label: t("Caller"),
        getValue: d => (
          <Flex gap="4px" align="center">
            <>
              <Text>{localisedValue({ object: d?.staff, key: "name" }) || "—"}</Text>
              {d?.staff?.id && <UserProfileSummary staffId={d?.staff?.id} />}
            </>
          </Flex>
        ),
      },
      {
        key: "callScore",
        label: t("Call score"),
        getValue: d => (d?.assessment?.scoring?.total_score || d?.totalScore) ?? "-",
        type: "aiScore",
      },
    ],
  },
})
